import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import ExportMenu from "@/components/ExportMenu";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import BookingWizard from "@/components/BookingWizard";
import { useNavigate } from "react-router-dom";
import { fmtDateTime, durationHours, formatMoney, fmtDate } from "@/lib/format";
import { CalendarPlus, Search, MapPin, Clock3, Activity, ArrowDownUp } from "lucide-react";
import { FilterPanel, FilterSection } from "@/components/ui/filter-popover";

const STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled", "no_show"];
const DEFAULT_BOOKING_FILTERS = { q: "", status: "all", start: "", end: "", min_price: "", max_price: "", location_id: "all", room_id: "all", service_id: "all", engineer_id: "all" };
const dateInput = (value) => value || "";
function SortHead({ label, field, sort, setSort }) {
    const active = sort.by === field;
    return <th className="px-4 py-3 text-left text-xs uppercase tracking-wider text-muted-foreground"><button className="inline-flex items-center gap-1 hover:text-foreground" onClick={() => setSort({ by: field, dir: active && sort.dir === "asc" ? "desc" : "asc" })}>{label}<ArrowDownUp className={`h-3 w-3 ${active ? "text-primary" : "opacity-40"}`} /></button></th>;
}

function BookingFilterFields({ filters, set, rooms, locations, services, staff }) {
  return (
    <>
      <FilterSection title="Search">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Search client, booking, room, notes" className="pl-8" />
        </div>
      </FilterSection>

      <FilterSection title="Status">
        <Select value={filters.status} onValueChange={(v) => set("status", v)}>
          <SelectTrigger><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s.replaceAll("_", " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Date Range">
        <div className="space-y-2">
          <Input id="bookings-from-date" type="date" value={dateInput(filters.start)} onChange={(e) => set("start", e.target.value)} aria-label="Bookings from date" placeholder="From date" />
          <Input id="bookings-through-date" type="date" value={dateInput(filters.end)} onChange={(e) => set("end", e.target.value)} aria-label="Bookings through date" placeholder="To date" />
        </div>
      </FilterSection>

      <FilterSection title="Price Range">
        <div className="grid grid-cols-2 gap-2">
          <Input type="number" min="0" value={filters.min_price} onChange={(e) => set("min_price", e.target.value)} placeholder="Min value" />
          <Input type="number" min="0" value={filters.max_price} onChange={(e) => set("max_price", e.target.value)} placeholder="Max value" />
        </div>
      </FilterSection>

      <FilterSection title="Location">
        <Select value={filters.location_id} onValueChange={(v) => set("location_id", v)}>
          <SelectTrigger><SelectValue placeholder="All locations" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All locations</SelectItem>
            {locations.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Room">
        <Select value={filters.room_id} onValueChange={(v) => set("room_id", v)}>
          <SelectTrigger><SelectValue placeholder="All rooms" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All rooms</SelectItem>
            {rooms.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Service">
        <Select value={filters.service_id} onValueChange={(v) => set("service_id", v)}>
          <SelectTrigger><SelectValue placeholder="All services" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All services</SelectItem>
            {services.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterSection>

      <FilterSection title="Staff">
        <Select value={filters.engineer_id} onValueChange={(v) => set("engineer_id", v)}>
          <SelectTrigger><SelectValue placeholder="All staff" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All staff</SelectItem>
            {staff.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </FilterSection>
    </>
  );
}

export default function BookingsPage() {
    const [filters, setFilters] = useState(DEFAULT_BOOKING_FILTERS);
    const [draftFilters, setDraftFilters] = useState(DEFAULT_BOOKING_FILTERS);
    const [sort, setSort] = useState({ by: "start_utc", dir: "asc" });
    const [wizardOpen, setWizardOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);

    const params = { ...filters, status: filters.status === "all" ? undefined : filters.status, location_id: filters.location_id === "all" ? undefined : filters.location_id, room_id: filters.room_id === "all" ? undefined : filters.room_id, service_id: filters.service_id === "all" ? undefined : filters.service_id, engineer_id: filters.engineer_id === "all" ? undefined : filters.engineer_id, start: filters.start || undefined, end: filters.end || undefined, min_price: filters.min_price || undefined, max_price: filters.max_price || undefined, sort_by: sort.by, sort_dir: sort.dir };
    const { data: bookings = [], refetch } = useQuery({ queryKey: ["bookings-all", params], queryFn: () => apiGet("/bookings", params) });
    const { data: clients = [] } = useQuery({ queryKey: ["clients"], queryFn: () => apiGet("/clients") });
    const { data: rooms = [] } = useQuery({ queryKey: ["rooms"], queryFn: () => apiGet("/rooms") });
    const { data: locations = [] } = useQuery({ queryKey: ["locations"], queryFn: () => apiGet("/locations") });
    const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => apiGet("/services") });
    const { data: staff = [] } = useQuery({ queryKey: ["staff"], queryFn: () => apiGet("/staff") });

    const byId = (rows) => Object.fromEntries(rows.map((row) => [row.id, row]));
    const clientById = useMemo(() => byId(clients), [clients]);
    const roomById = useMemo(() => byId(rooms), [rooms]);
    const locationById = useMemo(() => byId(locations), [locations]);
    const svcById = useMemo(() => byId(services), [services]);
    const staffById = useMemo(() => byId(staff), [staff]);

    const filtered = useMemo(() => bookings.filter((b) => {
        if (!filters.q) return true;
        const room = roomById[b.room_id]; const location = locationById[room?.location_id]; const text = filters.q.toLowerCase();
        return [b.id, b.notes, clientById[b.client_id]?.name, svcById[b.service_id]?.name, room?.name, location?.name].some((v) => String(v || "").toLowerCase().includes(text));
    }), [bookings, filters.q, clientById, svcById, roomById, locationById]);
    const setDraft = (key, value) => setDraftFilters((current) => ({ ...current, [key]: value }));
    const clearFilters = () => {
        setFilters(DEFAULT_BOOKING_FILTERS);
        setDraftFilters(DEFAULT_BOOKING_FILTERS);
        setFilterOpen(false);
    };
    const activeFilterCount = [
        filters.q ? 1 : 0,
        filters.status !== "all" ? 1 : 0,
        filters.start || filters.end ? 1 : 0,
        filters.min_price || filters.max_price ? 1 : 0,
        filters.location_id !== "all" ? 1 : 0,
        filters.room_id !== "all" ? 1 : 0,
        filters.service_id !== "all" ? 1 : 0,
        filters.engineer_id !== "all" ? 1 : 0
    ].reduce((a, b) => a + b, 0);

    const exportColumns = [{ header: "When", key: "when" }, { header: "Client", key: "client" }, { header: "Location", key: "location" }, { header: "Room", key: "room" }, { header: "Service", key: "service" }, { header: "Hours", key: "duration" }, { header: "Value", key: "value" }, { header: "Status", key: "status" }];
    const exportRows = filtered.map((b) => ({ when: fmtDateTime(b.start_utc, "MMM d, h:mm a"), client: clientById[b.client_id]?.name || "", location: locationById[roomById[b.room_id]?.location_id]?.name || "", room: roomById[b.room_id]?.name || "", service: svcById[b.service_id]?.name || "", duration: `${durationHours(b.start_utc, b.end_utc)}h`, value: formatMoney(b.price_total), status: b.status }));

    const navigate = useNavigate();
    const handleRowClick = (event) => {
        const interactive = event.target.closest('button, a, input, select, textarea, [role="button"]');
        if (interactive && interactive !== event.currentTarget) return;
        const id = event.currentTarget.dataset.id;
        if (id) navigate(`/bookings/${id}`);
    };
    const handleRowKeyDown = (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        const id = event.currentTarget.dataset.id;
        if (id) navigate(`/bookings/${id}`);
    };

    return <div className="musook-page w-full px-4 py-6 sm:px-6 lg:px-8" data-testid="bookings-page">
<PageHeader title="Bookings" testId="bookings-header" actions={<><ExportMenu key="export" filename="musook-bookings" title="Bookings" columns={exportColumns} rows={exportRows} sheetName="Bookings" /><Button key="filter" variant="outline" size="icon" aria-label="Filter bookings" onClick={() => setFilterOpen(!filterOpen)}><Search className="h-4 w-4" /></Button><Button key="new" onClick={() => setWizardOpen(true)}><CalendarPlus className="mr-2 h-4 w-4" />New booking</Button></>} />

<div className="mb-4 flex flex-wrap items-center gap-2">
          <FilterPanel
            activeCount={activeFilterCount}
            open={filterOpen}
            onOpenChange={(nextOpen) => { if (nextOpen) setDraftFilters(filters); setFilterOpen(nextOpen); }}
            onClear={clearFilters}
            onApply={() => { setFilters(draftFilters); setFilterOpen(false); }}
            title="Booking filters"
            mobileTitle="Filter bookings"
          >
            <BookingFilterFields filters={draftFilters} set={setDraft} rooms={rooms} locations={locations} services={services} staff={staff} />
          </FilterPanel>
          {activeFilterCount > 0 && <span className="text-xs text-muted-foreground">{activeFilterCount} active {activeFilterCount === 1 ? "filter" : "filters"}</span>}
        </div>

<Card className="musook-surface overflow-hidden rounded-xl border-border"><div className="overflow-x-auto pretty-scroll"><table className="min-w-full text-sm"><thead className="musook-table-head"><tr><SortHead label="When" field="start_utc" sort={sort} setSort={setSort} /><SortHead label="Client / activity" field="client_id" sort={sort} setSort={setSort} /><SortHead label="Studio / room" field="room_id" sort={sort} setSort={setSort} /><SortHead label="Service" field="service_id" sort={sort} setSort={setSort} /><SortHead label="Hours" field="end_utc" sort={sort} setSort={setSort} /><SortHead label="Value" field="price_total" sort={sort} setSort={setSort} /><SortHead label="Status" field="status" sort={sort} setSort={setSort} /></tr></thead><tbody className="divide-y divide-border">
  {filtered.map((b) => {
    const client = clientById[b.client_id];
    const room = roomById[b.room_id];
    const location = locationById[room?.location_id];
    const service = svcById[b.service_id];
    const engineer = staffById[b.engineer_id];
    return (
      <tr
        key={b.id}
        data-id={b.id}
        className="cursor-pointer hover:bg-muted/40"
        onClick={handleRowClick}
        onKeyDown={handleRowKeyDown}
        tabIndex={0}
        role="button"
        aria-label="Open booking details"
      >
        <td className="whitespace-nowrap px-4 py-3">
          <div>{fmtDateTime(b.start_utc, "MMM d, h:mm a")}</div>
          <div className="text-xs text-muted-foreground">{fmtDate(b.start_utc)}</div>
        </td>
        <td className="px-4 py-3">
          <div className="font-medium">{client?.name || "—"}</div>
          <div className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
            <Activity className="h-3 w-3" />
            {client?.last_interaction_at ? `Last activity ${fmtDateTime(client.last_interaction_at, "MMM d")}` : "No activity yet"}
          </div>
        </td>
        <td className="px-4 py-3">
          <div className="flex items-center gap-1 font-medium">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" />
            {location?.name || "Location not set"}
          </div>
          <div className="text-xs text-muted-foreground">{room?.name || "Room not set"}</div>
        </td>
        <td className="px-4 py-3">
          <div className="font-medium">{service?.name || "—"}</div>
          <div className="text-xs text-muted-foreground">{engineer?.name || "Unassigned"}</div>
        </td>
        <td className="whitespace-nowrap px-4 py-3">
          <div className="flex items-center gap-1">
            <Clock3 className="h-3.5 w-3.5 text-muted-foreground" />
            {durationHours(b.start_utc, b.end_utc)}h
          </div>
        </td>
        <td className="whitespace-nowrap px-4 py-3 font-medium">
          {formatMoney(b.price_total)}
        </td>
        <td className="px-4 py-3">
          <StatusPill status={b.status} />
        </td>
      </tr>
    );
  })}
</tbody></table>
{!filtered.length && <div className="p-10 text-center text-sm text-muted-foreground">No bookings match these filters.</div>}</div></Card>
        <Dialog open={wizardOpen} onOpenChange={setWizardOpen}><DialogContent className="max-w-3xl" aria-describedby={undefined}><DialogHeader><DialogTitle className="font-display">New booking</DialogTitle></DialogHeader><BookingWizard onSuccess={() => { setWizardOpen(false); refetch(); }} onCancel={() => setWizardOpen(false)} /></DialogContent></Dialog>
    </div>;
}